import { FC, useCallback, useEffect, useMemo } from "react"
import { graphql, HeadFC, PageProps } from "gatsby"
import { useDispatch, useSelector } from "react-redux"
import { Button, DocumentIcon, Typography } from "@eventbrite/marmalade"

// components
import { Layout } from "../components/Layout/Layout"
import { HeadUniversal } from "../components/HeadUniversal/HeadUniversal"

// utils
import {
  customRendererToRemovePTag,
  renderRichText
} from "../utils/contentful/render_rich_text"
import { events } from "../utils/heap/tracking_events"
import { assignLangCode, navigateWithPrefix } from "../utils/general_utils"
import {
  generateDescriptionMetaTag,
  generateRobotsMetaTag,
  getTitleText
} from "../utils/seo/seo_utils"

// types
import { ErrorPageMicroCopy } from "./404"
import { RootState } from "../store/store"

// styles
import {
  errorPageWrapper,
  header,
  helpCenterButton,
  secondaryText,
  iconWrapper,
  buttonContainer
} from "./errorPage.module.scss"

type TranslationErrorPageMicrocopySet = {
  messageTranslationErrorHeader: ErrorPageMicroCopy
  messageTranslationErrorSubheader: ErrorPageMicroCopy
  buttonReturnToHelpArticle: ErrorPageMicroCopy
  buttonBrowseHelpCenter: ErrorPageMicroCopy
}
const translationError: FC<PageProps<Queries.TranslationErrorPageQuery>> = (
  pageProps
) => {
  const data = pageProps?.data
  const userProfile = useSelector((state: RootState) => state.userProfile)

  const currentLangCode = useSelector(
    (state: RootState) => state.metaData.langCode
  )

  const dispatch = useDispatch()

  useEffect(() => {
    assignLangCode(currentLangCode, dispatch)
  }, [])

  useEffect(() => {
    if (userProfile) {
      events?.userProfileData("User Profile", userProfile)
    }
  }, [userProfile])

  const microCopy: TranslationErrorPageMicrocopySet | null = useMemo(() => {
    const objMap = data?.contentfulMicrocopySet?.microcopySetEntries?.map(
      (entry) => Object.values(entry as { [key: string]: ErrorPageMicroCopy })
    )
    if (!objMap) return null
    return Object.fromEntries(
      objMap as Iterable<[string, ErrorPageMicroCopy]>
    ) as TranslationErrorPageMicrocopySet
  }, [data?.contentfulMicrocopySet?.microcopySetEntries])

  const handleArticleButtonClick: React.MouseEventHandler<HTMLElement> =
    useCallback(() => {
      navigateWithPrefix(-1)
    }, [])
  const handleHelpCenterButtonClick: React.MouseEventHandler<HTMLElement> =
    useCallback(() => {
      navigateWithPrefix("/")
    }, [])

  return (
    <Layout
      gatsbyPageProps={pageProps}
      searchBar={{ isSearchBarVisible: true, isResultsBoxShown: true }}
      contact={false}
      microCopy={{
        buttonBrowseHelpCenter: data?.buttonBrowseHelpCenter,
        buttonContactUs: data?.buttonContactUs,
        messageContactUs: data?.messageContactUs,
        upperNavMicroCopySet: data?.upperNavMicroCopySet,
        headerSearchResults: data?.headerSearchResults,
        messageSearchBar: data?.messageSearchBar,
        chatbotUITranslations: data?.chatbotUITranslations
      }}
      footerCopy={data?.contentfulFooter?.footerReference ?? null}
      footerNavCopy={
        data?.contentfulFooterNavigation?.FooterNavigationChildItems ?? null
      }
    >
      <div className={errorPageWrapper}>
        <div className={iconWrapper}>
          <DocumentIcon size="xlarge" color="#3A3247" />
        </div>
        <Typography variant="heading-sm" className={header}>
          {renderRichText(
            microCopy?.messageTranslationErrorHeader?.raw,
            customRendererToRemovePTag
          )}
        </Typography>
        <Typography className={secondaryText}>
          {renderRichText(
            microCopy?.messageTranslationErrorSubheader?.raw,
            customRendererToRemovePTag
          )}
        </Typography>
        <div className={buttonContainer}>
          <div className={helpCenterButton}>
            <Button
              size="responsive"
              variant="secondary"
              onClick={handleArticleButtonClick}
            >
              {renderRichText(
                microCopy?.buttonReturnToHelpArticle?.raw,
                customRendererToRemovePTag
              )}
            </Button>
          </div>
          <div className={helpCenterButton}>
            <Button
              size="responsive"
              variant="primary"
              onClick={handleHelpCenterButtonClick}
            >
              {renderRichText(
                microCopy?.buttonBrowseHelpCenter?.raw,
                customRendererToRemovePTag
              )}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export const query = graphql`
  query TranslationErrorPage($node_locale: String! = "en-US") {
    # LAYOUT FIELDS
    chatbotUITranslations: allContentfulMicrocopySet(
      filter: {
        microcopySetInternalName: { eq: "Chatbot UI" }
        node_locale: { eq: $node_locale }
      }
    ) {
      edges {
        node {
          node_locale
          microcopySetEntries {
            ... on ContentfulMicrocopy {
              contentful_id
              microcopyKey
              microcopyValue {
                raw
              }
            }
          }
        }
      }
    }

    messageSearchBar: contentfulMicrocopy(
      microcopyKey: { eq: "messageSearchBar" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerSearchResults: contentfulMicrocopy(
      microcopyKey: { eq: "headerSearchResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    buttonContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "buttonContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    buttonBrowseHelpCenter: contentfulMicrocopy(
      microcopyKey: { eq: "buttonBrowseHelpCenter" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    messageContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "messageContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }

    upperNavMicroCopySet: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Help Center nav" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }

    contentfulFooter(node_locale: { eq: $node_locale }) {
      id
      footerInternalName
      footerReference {
        displayText
        FooterNavigationChildItems {
          footerNavigationInternalTitle
          displayText
          footerNavigationUrl
        }
      }
    }
    contentfulFooterNavigation(
      footerNavigationInternalTitle: { eq: "Category: Bottom row navigation" }
      node_locale: { eq: $node_locale }
    ) {
      footerNavigationInternalTitle
      footerNavigationUrl
      FooterNavigationChildItems {
        footerNavigationInternalTitle
        displayText
        footerNavigationUrl
      }
    }
    # END LAYOUT FIELDS

    contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Translation Error page" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetInternalName
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }
    contentfulSeoMetaTags(
      seoMetaTagInternalTitle: { eq: "translationErrorPageSeo" }
      node_locale: { eq: $node_locale }
    ) {
      seoMetaTagCrawlerFollow
      seoMetaTagDescription
      seoMetaTagRobotsIndex
      seoMetaTagTitle
    }
  }
`

export const Head: HeadFC<Queries.TranslationErrorPageQuery> = (headArgs) => (
  <>
    <HeadUniversal {...headArgs} disableOgTags={true} />
    {headArgs?.data?.contentfulSeoMetaTags && (
      <>
        {getTitleText(
          headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagTitle ?? ""
        )}

        {generateDescriptionMetaTag(
          headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagDescription ?? ""
        )}
        {generateRobotsMetaTag(
          headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagRobotsIndex ?? false,
          headArgs?.data?.contentfulSeoMetaTags?.seoMetaTagCrawlerFollow ?? false
        )}
      </>
    )}
  </>
)

export default translationError
